import he from 'he';

import type { WidgetRequestParameters } from '../../../assets/types/Request';
import { getSkip } from '../../../sharedModules/getSkip';
import { WidgetResult } from '../../hooks/useAnalytics';
import type { APIData } from '../../types/APIData';
import type { DealData } from '../../types/DealData';
import type { InitialiserResult } from '../../types/InitialiserResult';
import type { ModelInfo, ReviewData } from '../../types/ModelInfo';
import type { Tab } from '../../types/Tab';
import type { WidgetProps } from '../../types/WidgetProps';
import { WidgetType } from '../../types/WidgetType';
import type WidgetFeaturesIds from '../features/types/WidgetFeaturesIds';

import { formatSingleOffer } from './getFormattedOffers/formatSingleOffer';
import getModelCodeReviews from './getModelCodeReviews';
import setupTabs from './setupTabs';
import type { ModelWidgetData } from './types';

const getArea = (territory: string, area: string | null): string => {
  if (area) {
    return area;
  }
  if (territory) {
    return territory;
  }
  return 'GB';
};

export const hasValidData = (
  widgetResponse: { ok: boolean } | undefined,
  dealData: DealData | null,
  { showNoDeals }: Partial<WidgetFeaturesIds>,
): boolean => {
  return (
    widgetResponse?.ok === true &&
    ((dealData && dealData.deals && dealData.deals.length > 0) || showNoDeals === true)
  );
};

export const getModelWidget = async (
  setupModelWidgetData: ModelWidgetData,
): Promise<InitialiserResult> => {
  const { model, options } = setupModelWidgetData;
  const { localiser, features, props } = options;
  const { params, site, articleName, articleUrl, widget, modelBrand } = props;
  const { defaultTab, tabConfigs, widgetResponse, tabData, tabParams, widgetMissing } = model;
  const {
    models,
    battle,
    widget: widgetData,
  } = widgetResponse?.ok ? widgetResponse.data : { models: {}, battle: null };

  const area = getArea(props.territory, widgetResponse?.ok ? widgetResponse.data.area : null);

  const hasTabScopedParams =
    typeof tabParams === 'object' && Object.keys(tabParams as Record<string, unknown>).length > 0;

  const widgetProps: WidgetProps = {
    ...props,
    area,
    widgetIntroduction:
      props.widgetIntroduction &&
      he
        .decode(props.widgetIntroduction)
        .trim()
        .replace(/(\\n|\\)/g, ''),
    data: tabData,
    /* When widget.php is skipped (e.g. Tabletop), tabParams
      is never filled — keep API params for model.php */
    params: hasTabScopedParams ? tabParams : params,
    tabConfigs,
    defaultTab,
    getTabConfigs: (data: APIData, params: WidgetRequestParameters): Tab[] => {
      const { tabConfigs } = setupTabs({
        data,
        params,
        features,
        widget,
        localiser,
        models,
        area,
      });
      return tabConfigs;
    },
    models,
    reviews: await (async (): Promise<ReviewData[]> => {
      const keys = Object.keys(models || {});
      const value = defaultTab ? defaultTab.value : null;
      if (value && keys && keys.length > 0 && tabData[value] && tabData[value].model_info) {
        const modelId = keys
          .map((key) => models[key])
          .reduce((result, modelId) => {
            if (!result) {
              result = modelId;
            }
            return result;
          });
        return getModelCodeReviews(
          tabData[value].model_info[modelId] || {},
          features.showReviews ?? '',
          site,
          area,
          battle,
          props.articleType,
          props.articleCategory,
          props.social,
          props.trackingArea,
        );
      }
      return [];
    })(),
    getDealData: async (data): Promise<DealData> => {
      const localisedLinkText = features.linkText ? localiser.translate(features.linkText) : '';
      const offers = await Promise.all(
        (data?.offers ?? []).map(async (deal) =>
          formatSingleOffer({
            deal,
            localiser,
            localisedLinkText,
            showPrice: features.showPrice,
            showWasPrice: features.showWasPrice,
            customPromo: features.customPromo,
            customPromos: features.customPromos,
            excludeFrom: features.excludeFrom,
            showPromos: features.showPromos,
            site,
            area,
            articleName,
            articleUrl,
            articleType: props.articleType,
            articleCategory: props.articleCategory,
            trackingArea: props.trackingArea,
            params,
            widget,
            link: features.link,
            social: props.social,
          }),
        ),
      );

      // set up modelInfo for fallback
      // this is only used to determine the label for the sendAnalytics event (productblock widget)
      // we can remove it once we remove the sendAnalytics
      const modelInfo = data?.model_info ?? {
        0: {
          brand: modelBrand,
          model_name: params?.model_name,
        },
      };

      return {
        deals: offers,
        totalDeals: data?.counts?.[data.offer_type],
        dealType: data?.offer_type,
        modelInfo: {
          ...modelInfo,
          ...widgetData?.data.model_info,
        } as ModelInfo,
      };
    },
  };
  widgetProps.dealData = await widgetProps.getDealData(defaultTab ? tabData[defaultTab.value] : {});

  if (
    !widgetMissing &&
    defaultTab &&
    hasValidData(model.widgetResponse, widgetProps.dealData, features)
  ) {
    const skipWidget = getSkip(widgetProps, options, widgetProps.widget);
    if (skipWidget) {
      return {
        props: widgetProps,
        result: WidgetResult.SKIPPED,
      };
    }
    return {
      props: widgetProps,
      result: WidgetResult.SUCCESS,
    };
  }

  const { only_fallback_offers } = params;

  if (
    options.props.widget.type === WidgetType.DEAL ||
    options.props.widget.type === WidgetType.ANF_DEAL ||
    (only_fallback_offers && defaultTab) ||
    // A collection widget with data-only-fallback-offers is expected to
    // contain all data in data-hawk-json
    (only_fallback_offers &&
      (options.props.widget.type === WidgetType.COLLECTION ||
        options.props.widget.type === WidgetType.ANF_COLLECTION))
  ) {
    return {
      props: widgetProps,
      result: WidgetResult.SUCCESS,
    };
  }

  const widgetRequestSkipped = !widgetResponse;

  if (widgetRequestSkipped) {
    return {
      props: widgetProps,
      result: WidgetResult.SKIPPED,
    };
  }

  const widgetRequestFailed = !widgetResponse.ok;
  if (widgetRequestFailed) {
    return {
      props: widgetProps,
      result: WidgetResult.PRESERVED,
    };
  }

  return {
    props: widgetProps,
    result: WidgetResult.MISSING,
  };
};
